import { useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, auth } from '@/firebase';

export interface VaultDocument {
  id: string;
  name: string;
  storagePath: string;
  downloadUrl: string;
  tags: string[];
  uploadedBy: string;
  uploadedAt: any;
  size: number;
}

export function useDocumentVault(householdId: string | null): {
  documents: VaultDocument[];
  loading: boolean;
  uploadProgress: number;
  upload: (file: File, tags: string[]) => Promise<void>;
  deleteDocument: (doc: VaultDocument) => Promise<void>;
} {
  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (!householdId) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'households', householdId, 'documents'),
      orderBy('uploadedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs: VaultDocument[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        docs.push({
          id: docSnap.id,
          name: data.name || '',
          storagePath: data.storagePath || '',
          downloadUrl: data.downloadUrl || '',
          tags: data.tags || [],
          uploadedBy: data.uploadedBy || 'unknown',
          uploadedAt: data.uploadedAt,
          size: data.size || 0,
        });
      });
      setDocuments(docs);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching documents:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [householdId]);

  const upload = async (file: File, tags: string[]): Promise<void> => {
    if (!householdId) throw new Error('No household selected');
    
    // Check file size
    if (file.size > 10 * 1024 * 1024) {
      throw new Error('File exceeds 10 MB limit');
    }

    const uuid = crypto.randomUUID();
    const storage = getStorage();
    const storagePath = `households/${householdId}/documents/${uuid}_${file.name}`;
    const storageRef = ref(storage, storagePath);

    // Upload to storage
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          setUploadProgress(0);
          reject(error);
        },
        async () => {
          try {
            const downloadUrl = await getDownloadURL(storageRef);
            
            await addDoc(collection(db, 'households', householdId, 'documents'), {
              name: file.name,
              storagePath,
              downloadUrl,
              tags,
              uploadedBy: auth.currentUser?.uid || 'unknown',
              uploadedAt: serverTimestamp(),
              size: file.size,
            });
            
            setUploadProgress(0);
            resolve();
          } catch (error) {
            setUploadProgress(0);
            reject(error);
          }
        }
      );
    });
  };

  const deleteDocument = async (document: VaultDocument): Promise<void> => {
    if (!householdId) throw new Error('No household selected');

    const storage = getStorage();
    await deleteObject(ref(storage, document.storagePath));
    await deleteDoc(doc(db, 'households', householdId, 'documents', document.id));
  };

  return {
    documents,
    loading,
    uploadProgress,
    upload,
    deleteDocument,
  };
}